import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { useWebPush } from "../hooks/useWebPush";

const SNOOZE_KEY = "gg-push-prompt-snoozed";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function isSnoozed(): boolean {
  try {
    const val = localStorage.getItem(SNOOZE_KEY);
    if (!val) return false;
    return Date.now() - parseInt(val, 10) < SNOOZE_MS;
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
  } catch {
    // ignore storage errors
  }
}

function clearSnooze(): void {
  try {
    localStorage.removeItem(SNOOZE_KEY);
  } catch {
    // ignore storage errors
  }
}

export function PushNotificationBanner() {
  const push = useWebPush();
  const [dismissed, setDismissed] = useState(true);

  // Drive visibility purely from the *live* push status. The hook keeps
  // `subscribed`/`permission` in sync with the browser, so the banner appears
  // only when notifications are genuinely off and hides itself the moment they
  // are enabled (even from another tab) — no more nagging out of sync.
  useEffect(() => {
    if (!push.ready) return;
    if (!push.available) return;
    if (push.subscribed) return;
    if (push.permission === "denied") return;
    if (isSnoozed()) return;
    // Defer revealing the prompt: right after login the subscription is still
    // settling (and self-healing may re-create an expired one). If push turns
    // out to be already enabled, `subscribed` flips to true within this window,
    // this effect re-runs, the timeout is cleared and the prompt never shows —
    // killing the flash. Only a genuinely-off state survives the delay.
    const t = setTimeout(() => setDismissed(false), 1500);
    return () => clearTimeout(t);
  }, [push.ready, push.available, push.subscribed, push.permission]);

  useEffect(() => {
    if (push.subscribed) {
      // Reset the snooze so that if push is ever turned off again the prompt is
      // free to reappear in line with the actual status.
      clearSnooze();
      setDismissed(true);
    }
  }, [push.subscribed]);

  if (dismissed) return null;

  function handleEnable() {
    void push.enable();
  }

  function handleDismiss() {
    snooze();
    setDismissed(true);
  }

  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg bg-white border border-gray-200 shadow-lg px-4 py-3 text-sm w-[calc(100%-2rem)] max-w-sm">
      <Bell size={16} className="text-amber-500 flex-shrink-0" />
      <p className="flex-1 text-gray-700 leading-snug">
        Włącz powiadomienia push, aby nie przeoczyć wiadomości w GG.
        {push.error && (
          <span role="alert" className="mt-1 block text-xs text-red-600">
            {push.error}
          </span>
        )}
      </p>
      <button
        type="button"
        disabled={push.busy}
        onClick={handleEnable}
        className="flex-shrink-0 text-xs font-medium text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 rounded px-3 py-1.5"
      >
        Włącz
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        title="Nie teraz"
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
      >
        <X size={14} />
      </button>
    </div>
  );
}
